import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import Placeholder from "@/components/Placeholder";

const PILLARS = [
  {
    key: "service",
    title: "Service",
    emoji: "🤝",
    body: "Service is where it all begins for us. Every initiative is rooted in real needs — whether it's hunger, education, the environment, or animal welfare. Through this, we experience what it truly means to show up for others — and that's where real impact begins.",
  },
  {
    key: "leadership",
    title: "Leadership",
    emoji: "🦁",
    body: "Leadership here isn't assigned — it's discovered. It grows through action, responsibility, and stepping forward when it matters. Over time, these moments shape how we lead.",
  },
  {
    key: "fellowship",
    title: "Fellowship",
    emoji: "🫂",
    body: "Fellowship is what makes this more than just a club. It's in the friendships, shared experiences, and the sense of belonging we build together.",
  },
];

export default function About() {
  return (
    <div className="pt-32 md:pt-40">
      {/* HEADER */}
      <section className="pb-20 md:pb-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-6">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Our story
            </div>
            <h1 className="font-playfair text-5xl md:text-7xl lg:text-[8rem] font-semibold text-[#2C2C2C] leading-[0.95] tracking-tight max-w-5xl">
              A story built on <em className="text-[#8B1E2D]">showing up</em>.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* STORY + IMAGE */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-7 md:col-start-1 space-y-6 font-inter text-[#555555] text-lg leading-[1.75]">
            <p>
              The Leo Club of Mumbai Carter Road began in <span className="text-[#8B1E2D] font-medium">December 2017</span> with a simple idea — to create a space where young people could come together, not just to talk about change, but to be a part of it.
            </p>
            <p>
              What started as a small group has grown into a community shaped by shared experiences — late-night planning, early mornings, meaningful work, and moments that stay with you long after.
            </p>
            <p>
              Over time, we've shown up in many ways — serving meals, supporting education, caring for the environment, and simply being present where it mattered. But beyond the work, it's the people and the journey that define us.
            </p>
            <p className="font-playfair italic text-2xl md:text-3xl text-[#2C2C2C] leading-snug pt-4 border-l-2 border-[#F4C430] pl-6">
              Being a Leo isn't about doing something extraordinary every time. It's about showing up, consistently, and growing through it — together.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
            <div className="sticky top-32 space-y-4">
              <div className="aspect-[3/4] w-full overflow-hidden">
                <img
                  src="/images/about/about-main.jpg"
                  alt="Leo Club Mumbai Carter Road members"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src="/images/about/about-community.jpg"
                    alt="Leo community"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src="/images/about/about-action.jpg"
                    alt="Leo in action"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT DRIVES US */}
      <section className="py-24 md:py-32 bg-[#F7F3EE]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> What drives us
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-[1.05]">
              Measured in <em className="text-[#8B1E2D]">moments</em>, not just numbers.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7 space-y-6 font-inter text-[#555555] text-lg leading-[1.75] md:pt-6">
            <p>
              We believe meaningful change doesn't come from big gestures alone, but from consistent effort and genuine intent.
            </p>
            <p>
              We measure our work not just in numbers, but in moments — the lives we touch, the awareness we create, and the impact we quietly become a part of.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal className="mb-16">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Our pillars
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-[1.05] max-w-3xl">
              <em className="text-[#8B1E2D]">Leadership.</em> Experience. Opportunity.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {PILLARS.map((p) => (
              <StaggerItem key={p.key}>
                <article
                  className="group relative h-full rounded-2xl p-9 md:p-10 bg-white border-[1.5px] border-[rgba(244,196,48,0.4)] transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:border-2 hover:shadow-[0_0_12px_rgba(244,196,48,0.3)]"
                  data-testid={`pillar-${p.key}`}
                >
                  <div
                    className="mb-7 leading-none select-none"
                    style={{ fontSize: "44px" }}
                    aria-hidden="true"
                  >
                    {p.emoji}
                  </div>
                  <h3 className="font-playfair text-3xl md:text-4xl font-bold text-[#2C2C2C] leading-tight mb-5">
                    {p.title}
                  </h3>
                  <p className="font-inter text-[#555555] text-base leading-relaxed">
                    {p.body}
                  </p>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* LEGACY */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-100" style={{ mixBlendMode: "overlay" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430] mb-5">
              <span className="w-8 h-px bg-[#F4C430] inline-block align-middle mr-3" /> Legacy
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Carrying a <em className="text-[#F4C430]">global story</em> forward.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7 space-y-6 font-inter text-white/80 text-lg leading-[1.75] md:pt-6">
            <p>
              We are part of <span className="text-[#F4C430]">Lions Clubs International</span>, a global network built on service and leadership across generations.
            </p>
            <p>For many of us, Leo is where it begins.</p>
            <p>
              And when we step forward into Lions, we carry this with us — the people, the lessons, and the journey.
            </p>
            <p className="font-playfair italic text-2xl md:text-3xl text-white leading-snug pt-4 border-l-2 border-[#F4C430] pl-6">
              The legacy we build here isn't just in what we do — but in who we become.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
